import React from 'react';
import { Dialog } from '@mui/material';
import styled, { keyframes } from 'styled-components';
import { CloseIcon } from './Icons';
import { appDialogTone } from './appDialogTone';
import { useIsAdminSurface } from '../../hooks/useIsAdminSurface';

/**
 * The shell every dialog in the form flow is built on. MUI's DialogTitle /
 * DialogContent / DialogActions default to Material's own type scale and
 * uppercase text buttons, which read as a different product next to the rest of
 * this UI — so the chrome lives here once and each dialog supplies only its
 * body and its buttons.
 */

export interface AppDialogAction {
  label: string;
  onClick: () => void;
  /** One filled button per dialog: the action the user is expected to take. */
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  icon?: React.ReactNode;
}

interface AppDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: React.ReactNode;
  /** Circular badge above the title. Omit for dialogs that are pure text. */
  icon?: React.ReactNode;
  actions?: AppDialogAction[];
  /** Buttons span the full width; set false to keep them right-aligned. */
  stretchActions?: boolean;
  maxWidth?: 'xs' | 'sm';
  children?: React.ReactNode;
}

const Sheet = styled.div<{ $admin: boolean }>`
  ${(p) => appDialogTone(p.$admin)}
  width: 100%;
  padding: 36px 32px 28px;
  position: relative;
  font-family: inherit;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: var(--dialog-muted);
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s ease, color 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
  }

  &:hover {
    background: var(--dialog-close-hover);
    color: var(--dialog-close-hover-text);
  }

  &:active,
  &:focus {
    outline: none;
    box-shadow: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px var(--dialog-ring);
  }
`;

const IconBadge = styled.div`
  width: 52px;
  height: 52px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: var(--dialog-accent-soft);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
    stroke: var(--dialog-accent);
  }
`;

const Title = styled.h2`
  margin: 0 0 4px;
  font-size: 19px;
  font-weight: 700;
  color: var(--dialog-text);
  text-align: center;
`;

const Subtitle = styled.div<{ $spaced: boolean }>`
  margin: 0 0 ${(p) => (p.$spaced ? '26px' : '0')};
  font-size: 13.5px;
  color: var(--dialog-muted);
  text-align: center;
  line-height: 1.5;
`;

const ButtonRow = styled.div<{ $stretch: boolean }>`
  display: flex;
  justify-content: ${(p) => (p.$stretch ? 'stretch' : 'flex-end')};
  gap: 10px;
  margin-top: 26px;
`;

const BaseButton = styled.button<{ $stretch: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: ${(p) => (p.$stretch ? '1' : '0 0 auto')};
  padding: 10px ${(p) => (p.$stretch ? '10px' : '22px')};
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  outline: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease,
    opacity 0.15s ease;

  &:active,
  &:focus {
    outline: none;
    box-shadow: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SecondaryButton = styled(BaseButton)`
  border: 1px solid var(--dialog-border);
  background: var(--dialog-surface);
  color: var(--dialog-secondary-text);

  &:hover {
    background: var(--dialog-hover);
    border-color: var(--dialog-border-strong);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px var(--dialog-ring);
  }
`;

const PrimaryButton = styled(BaseButton)`
  border: none;
  background: var(--dialog-accent);
  color: #fff;

  &:hover {
    background: var(--dialog-accent-hover);
  }

  &:active,
  &:focus {
    background: var(--dialog-accent-active);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px var(--dialog-ring);
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const AppDialogSpinner = styled.span`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

export const AppDialog: React.FC<AppDialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  actions,
  stretchActions = true,
  maxWidth = 'xs',
  children,
}) => {
  const isAdminSurface = useIsAdminSurface();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{ style: { borderRadius: 16 } }}
    >
      <Sheet $admin={isAdminSurface}>
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </CloseButton>

        {icon && <IconBadge>{icon}</IconBadge>}

        <Title>{title}</Title>
        {/* A dialog with no body needs no gap under its subtitle — the button
            row supplies the spacing instead. */}
        {subtitle && <Subtitle $spaced={Boolean(children)}>{subtitle}</Subtitle>}

        {children}

        {actions && actions.length > 0 && (
          <ButtonRow $stretch={stretchActions}>
            {actions.map((action) => {
              const Button =
                action.variant === 'secondary' ? SecondaryButton : PrimaryButton;
              return (
                <Button
                  key={action.label}
                  type="button"
                  $stretch={stretchActions}
                  onClick={action.onClick}
                  disabled={action.disabled}
                >
                  {action.icon}
                  {action.label}
                </Button>
              );
            })}
          </ButtonRow>
        )}
      </Sheet>
    </Dialog>
  );
};

export default AppDialog;
